// Advanced performance optimizations for better LCP

// Critical CSS inlining
export const inlineCriticalCSS = () => {
  const criticalCSS = `
    .App { 
      background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0f172a 100%);
      min-height: 100vh;
      overflow-x: hidden;
    }
    .hero-section { 
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .loading-screen {
      position: fixed;
      inset: 0;
      z-index: 50;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #111827;
    }
  `;
  
  const style = document.createElement('style');
  style.textContent = criticalCSS;
  document.head.appendChild(style);
};

// Optimize images loading
export const optimizeImageLoading = () => {
  // Use Intersection Observer for lazy loading
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            imageObserver.unobserve(img);
          }
        }
      });
    }, { rootMargin: '100px' }); // Increased rootMargin for better performance

    // Observe all lazy images
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
  
  // Add loading="lazy" to all images for native lazy loading
  document.querySelectorAll('img:not([loading])').forEach(img => {
    if (!img.classList.contains('critical')) {
      img.loading = 'lazy';
    }
  });
  
  // Optimize image sizes based on viewport
  const optimizeImageSizes = () => {
    const images = document.querySelectorAll('img[data-srcset]');
    images.forEach(img => {
      const srcset = img.dataset.srcset;
      if (srcset) {
        img.srcset = srcset;
        img.sizes = img.dataset.sizes || '100vw';
      }
    });
  };
  
  // Run optimization after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', optimizeImageSizes);
  } else {
    optimizeImageSizes();
  }
};

// Optimize animations for better performance
export const optimizeAnimations = () => {
  // Reduce motion for users who prefer it
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  
  if (prefersReducedMotion.matches) {
    document.documentElement.style.setProperty('--animation-duration', '0.1s');
    document.documentElement.style.setProperty('--transition-duration', '0.1s');
  }
  
  // Use hardware acceleration
  const style = document.createElement('style');
  style.textContent = `
    .hardware-accelerated {
      will-change: transform, opacity;
      transform: translateZ(0);
      backface-visibility: hidden;
    }
    
    .smooth-scroll {
      scroll-behavior: smooth;
    }
    
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
  document.head.appendChild(style);
};

// Optimize scroll performance
export const optimizeScrollPerformance = () => {
  let ticking = false;
  
  const updateScroll = () => {
    // Optimize scroll-based animations
    const scrollElements = document.querySelectorAll('[data-scroll]');
    scrollElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible) {
        element.classList.add('in-view');
      }
    });
    
    ticking = false;
  };
  
  const requestTick = () => {
    if (!ticking) {
      requestAnimationFrame(updateScroll);
      ticking = true;
    }
  };
  
  window.addEventListener('scroll', requestTick, { passive: true });
};

// Optimize font loading
export const optimizeFontLoading = () => {
  // Preload critical fonts with font-display: swap
  const fontLinks = [
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
    'https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500&display=swap'
  ];
  
  fontLinks.forEach(href => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = href;
    link.onload = () => link.rel = 'stylesheet';
    document.head.appendChild(link);
  });
  
  // Add font-display: swap for better performance
  const fontDisplayStyle = document.createElement('style');
  fontDisplayStyle.textContent = `
    @font-face {
      font-family: 'Inter';
      font-display: swap;
    }
    @font-face {
      font-family: 'Roboto Mono';
      font-display: swap;
    }
  `;
  document.head.appendChild(fontDisplayStyle);
};

// Optimize bundle splitting
export const optimizeBundleSplitting = () => {
  // Dynamic imports for non-critical components
  const loadComponent = async (componentName) => {
    try {
      const module = await import(`../components/${componentName}.jsx`);
      return module.default;
    } catch (error) {
      console.warn(`Failed to load component: ${componentName}`, error);
      return null;
    }
  };
  
  // Preload components based on user interaction
  const preloadComponents = () => {
    const components = ['Projects', 'Contact', 'Experience'];
    components.forEach(component => {
      loadComponent(component);
    });
  };
  
  // Start preloading after initial load
  setTimeout(preloadComponents, 1000);
};

// Initialize all advanced optimizations
export const initAdvancedPerformance = () => {
  inlineCriticalCSS();
  optimizeImageLoading();
  optimizeAnimations();
  optimizeScrollPerformance();
  optimizeFontLoading();
  optimizeBundleSplitting();
  
  // Performance monitoring
  if ('performance' in window) {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0];
      const paint = performance.getEntriesByType('paint');
      
      console.log('Performance Metrics:', {
        TTFB: navigation.responseStart - navigation.requestStart,
        LCP: paint.find(entry => entry.name === 'largest-contentful-paint')?.startTime,
        FCP: paint.find(entry => entry.name === 'first-contentful-paint')?.startTime
      });
    });
  }
};
